import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiBoggle } from '../utils/api';
import SeoContent from '../components/SeoContent';
import DefinableWord from '../components/DefinableWord';
import PageSEO from '../components/PageSEO';

const SIZES = [
  { label: '3×3', value: 3 },
  { label: '4×4', value: 4 },
  { label: '5×5', value: 5 },
  { label: '6×6', value: 6 },
];

const DICE_LETTERS = 'AAABCDDEEEEFFFFGGHIIIJKLLMMNNNOOOPPQRRSSTTTUUUVVWWXYYZ';

function makeEmpty(n) {
  return Array.from({ length: n }, () => Array.from({ length: n }, () => ''));
}

function cellSize(n) {
  return n === 3 ? 70 : n === 4 ? 60 : n === 5 ? 50 : 44;
}

export default function BoggleSolver() {
  const [size, setSize]       = useState(4);
  const [grid, setGrid]       = useState(makeEmpty(4));
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [minLen, setMinLen]   = useState(3);

  function changeSize(n) {
    setSize(n);
    setGrid(makeEmpty(n));
    setResults(null);
    setError('');
  }

  function clearGrid() {
    setGrid(makeEmpty(size));
    setResults(null);
    setError('');
  }

  const updateCell = useCallback((r, c, val, currentGrid, currentSize) => {
    const letter = val.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(-1);
    const g = currentGrid.map(row => [...row]);
    g[r][c] = letter;
    setGrid(g);
    if (letter) {
      const next = r * currentSize + c + 1;
      if (next < currentSize * currentSize) {
        const inputs = document.querySelectorAll('.boggle-cell');
        inputs[next]?.focus();
        inputs[next]?.select();
      }
    }
  }, []);

  function randomize() {
    setGrid(Array.from({ length: size }, () =>
      Array.from({ length: size }, () => DICE_LETTERS[Math.floor(Math.random() * DICE_LETTERS.length)])
    ));
    setResults(null);
  }

  async function handleSolve() {
    const flat = grid.flat();
    if (flat.some(c => !c)) {
      setError('Please fill in all cells before solving.');
      return;
    }
    setLoading(true); setError(''); setResults(null);
    try {
      const { data } = await apiBoggle(grid);
      setResults(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong.');
    } finally { setLoading(false); }
  }

  const cs = cellSize(size);
  const gridMaxWidth = size * cs + (size - 1) * 8 + 2;

  const filtered = results?.results?.filter(r => r.length >= minLen) || [];
  const grouped  = filtered.reduce((acc, r) => { (acc[r.length] = acc[r.length] || []).push(r); return acc; }, {});

  return (
    <div className="game-page">
      <PageSEO page="boggle" />
      <div className="container" style={{ maxWidth: 780 }}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🎯</span>
          <h1>Boggle Solver</h1>
          <p>Choose a board size, enter your letters, and find every valid word you can trace through adjacent cells!</p>
        </div>

        <div className="solver-card">
          {/* Grid size selector */}
          <div className="d-flex align-items-center gap-2 mb-4 justify-content-center flex-wrap">
            <span className="fw-bold text-muted me-1">Board size:</span>
            {SIZES.map(({ label, value }) => (
              <button
                key={value}
                type="button"
                onClick={() => changeSize(value)}
                className="btn btn-sm fw-bold"
                style={{
                  borderRadius: '50px',
                  padding: '4px 16px',
                  background: size === value ? 'var(--primary)' : 'var(--border)',
                  color: size === value ? '#fff' : 'var(--text)',
                  border: 'none',
                  fontFamily: 'Nunito,sans-serif',
                }}
              >{label}</button>
            ))}
          </div>

          {/* Grid */}
          <div className="text-center mb-3">
            <div
              className="boggle-grid mx-auto"
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${size}, 1fr)`,
                gap: '8px',
                maxWidth: `${gridMaxWidth}px`,
              }}
            >
              {grid.map((row, r) => row.map((cell, c) => (
                <input
                  key={`${r}-${c}`}
                  value={cell}
                  onChange={e => updateCell(r, c, e.target.value, grid, size)}
                  onFocus={e => e.target.select()}
                  placeholder="·"
                  className="boggle-cell"
                  style={{
                    width: `${cs}px`,
                    height: `${cs}px`,
                    textAlign: 'center',
                    border: '2px solid var(--border)',
                    borderRadius: '10px',
                    fontSize: size >= 5 ? '1.1rem' : '1.4rem',
                    fontWeight: 900,
                    fontFamily: 'Fredoka One,cursive',
                    color: 'var(--primary)',
                    background: '#fff',
                    cursor: 'text',
                    textTransform: 'uppercase',
                  }}
                  maxLength={1}
                />
              )))}
            </div>
          </div>

          {/* Action buttons */}
          <div className="d-flex justify-content-center gap-2 flex-wrap mb-4">
            <button
              type="button"
              className="btn btn-sm fw-bold"
              style={{ borderRadius: '50px', background: 'var(--border)', border: 'none', fontFamily: 'Nunito,sans-serif' }}
              onClick={randomize}
            >🎲 Random Board</button>
            <button
              type="button"
              className="btn btn-sm fw-bold"
              style={{ borderRadius: '50px', background: '#fee2e2', color: '#dc2626', border: 'none', fontFamily: 'Nunito,sans-serif' }}
              onClick={clearGrid}
            >✕ Clear Board</button>
          </div>

          {/* Min word length */}
          <div className="d-flex align-items-center gap-3 mb-4 justify-content-center flex-wrap">
            <span className="fw-bold text-muted">Min word length:</span>
            {[3, 4, 5, 6].map(n => (
              <button key={n} type="button"
                onClick={() => setMinLen(n)}
                className="btn btn-sm fw-bold"
                style={{
                  borderRadius: '50px', width: '36px', height: '36px', padding: 0,
                  background: minLen === n ? 'var(--primary)' : 'var(--border)',
                  color: minLen === n ? '#fff' : 'var(--text)', border: 'none',
                  fontFamily: 'Nunito,sans-serif',
                }}
              >{n}</button>
            ))}
          </div>

          <div className="text-center">
            <button className="solve-btn" onClick={handleSolve} disabled={loading}>
              {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Solving…</> : '🎯 Find All Words'}
            </button>
          </div>
        </div>

        {error && <div className="alert alert-danger alert-custom">{error}</div>}

        {results !== null && (
          <div className="solver-card">
            <p className="result-count">
              Found <strong>{filtered.length}</strong> words (min {minLen} letters) on a {size}×{size} board
            </p>
            {filtered.length === 0 ? (
              <p className="text-muted">No words of length {minLen}+ found. Try a different board or lower minimum.</p>
            ) : (
              Object.keys(grouped).sort((a, b) => b - a).map(len => (
                <div key={len} className="mb-3">
                  <h6 className="fw-bold text-muted mb-2">{len}-letter words ({grouped[len].length})</h6>
                  <div className="words-cloud">
                    {grouped[len].map(r => (
                      <DefinableWord key={r.word} word={r.word} className="result-badge" style={{textDecoration:'none'}}>{r.word.toUpperCase()}</DefinableWord>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <SeoContent game="boggle" />
      </div>
    </div>
  );
}
